

import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Box, IconButton, Checkbox } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ContactInterface } from '../types';
import ImportOptionsDialog from './ImportOptionsDialog';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WhatsAppModal from './WhatsAppModal';

interface ContactsTableProps {
  contacts: ContactInterface[];
  loading: boolean;
  onContactClick: (contact: ContactInterface) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
  selectedContactIds?: string[];
  onSelectContact?: (contactId: string, checked: boolean) => void;
  onExport?: () => void;
  onImport?: (data: any[]) => Promise<void>;
  onDownloadTemplate?: () => void;
}

function ContactsTable({ contacts, loading, onContactClick, page, pageSize, total, onPageChange, selectedContactIds, onSelectContact, onExport, onImport, onDownloadTemplate }: ContactsTableProps): React.JSX.Element {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importOptionsOpen, setImportOptionsOpen] = React.useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = React.useState(false);
  const [selectedContactForWhatsApp, setSelectedContactForWhatsApp] = React.useState<{ name: string, phone: string } | null>(null);


  const totalPages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <TableContainer component={Paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">אנשי קשר</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onExport && (
            <IconButton
              color="primary"
              onClick={onExport}
              title="ייצוא ל-CSV"
              sx={{
                backgroundColor: 'grey.300',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'grey.400' }
              }}
            >
              <DownloadIcon />
            </IconButton>
          )}
          {onImport && (
            <>
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const Papa = await import('papaparse');
                  Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: async (results) => {
                      if (results.data) {
                        await onImport(results.data);
                      }
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  });
                }}
              />
              <IconButton
                color="primary"
                onClick={() => {
                  if (onDownloadTemplate) {
                    setImportOptionsOpen(true);
                  } else {
                    fileInputRef.current?.click();
                  }
                }}
                title="ייבוא מ-CSV"
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'grey.400' }
                }}
              >
                <UploadFileIcon />
              </IconButton>
              <ImportOptionsDialog
                open={importOptionsOpen}
                onClose={() => setImportOptionsOpen(false)}
                onImportClick={() => fileInputRef.current?.click()}
                onDownloadTemplateClick={() => onDownloadTemplate?.()}
                title="אפשרויות ייבוא אנשי קשר"
              />
            </>
          )}
        </Box>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            {onSelectContact && <TableCell padding="checkbox"></TableCell>}
            <TableCell>ID</TableCell>
            <TableCell>שם שחיין</TableCell>
            <TableCell>גיל</TableCell>
            <TableCell>טלפון הורה</TableCell>
            <TableCell>אימייל הורה</TableCell>
            <TableCell>שם הורה</TableCell>
            {contacts.some(c => c.amount !== undefined) && <TableCell>סכום ששולם</TableCell>}
            <TableCell>נוצר בתאריך</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={onSelectContact ? (contacts.some(c => c.amount !== undefined) ? 9 : 8) : (contacts.some(c => c.amount !== undefined) ? 8 : 7)} align="center" sx={{ py: 3 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : contacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={onSelectContact ? (contacts.some(c => c.amount !== undefined) ? 9 : 8) : (contacts.some(c => c.amount !== undefined) ? 8 : 7)} align="center" sx={{ py: 3 }}>
                לא נמצאו אנשי קשר
              </TableCell>
            </TableRow>
          ) : (
            contacts.map((contact) => (
              <TableRow
                key={contact.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={() => onContactClick(contact)}
              >
                {onSelectContact && (
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedContactIds?.includes(contact.id) || false}
                      onChange={e => onSelectContact(contact.id, e.target.checked)}
                    />
                  </TableCell>
                )}
                <TableCell>{contact.id}</TableCell>
                <TableCell>{contact.name}</TableCell>
                <TableCell>{contact.age}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {contact.phone}
                    {contact.phone && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedContactForWhatsApp({ name: contact.name, phone: contact.phone });
                          setWhatsAppModalOpen(true);
                        }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.parent_name}</TableCell>
                {contacts.some(c => c.amount !== undefined) && (
                  <TableCell>
                    {contact.amount !== undefined ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'ILS'
                    }).format(contact.amount) : ''}
                  </TableCell>
                )}
                <TableCell>{new Date(contact.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {/* Pagination UI */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="body2">
          מציג {from}-{to} מתוך {total} פריטים
        </Typography>
        <Box>
          <IconButton onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
            {'<'}
          </IconButton>
          <Typography variant="body2" component="span" sx={{ mx: 1 }}>
            עמוד {page} מתוך {totalPages}
          </Typography>
          <IconButton onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
            {'>'}
          </IconButton>
        </Box>
      </Box>
      {selectedContactForWhatsApp && (
        <WhatsAppModal
          open={whatsAppModalOpen}
          onClose={() => {
            setWhatsAppModalOpen(false);
            setTimeout(() => setSelectedContactForWhatsApp(null), 300);
          }}
          contactName={selectedContactForWhatsApp.name}
          contactNumber={selectedContactForWhatsApp.phone}
        />
      )}
    </TableContainer>
  );
}

export default ContactsTable; 